<script setup lang="ts">
import { EStoreModules } from '@/store/storeType'
import { EHubProvider, type EventDetailProvider } from '@/types/enum/HubProviderEnum'
import type { AssociatedDeviceModel, DeviceModel } from '@/types/models/DeviceModel'
import type { EventModel } from '@/types/models/EventModel'
import {
    BaseDialog,
    CheckboxField,
    FullScreenLoader,
    PrimaryButton,
    StackLayout,
    TypographyItem
} from '@rfc/rfc-components-library'
import { EDeviceActions } from '@rfc/rfc-components-library/store'
import { computed, inject, ref } from 'vue'
import { useStore } from 'vuex'
import { defaultEFiltersDevices } from '../../../../types/enum/FiltersDeviceEnum'

const store = useStore()

type Props = {
    data: EventModel
}
defineProps<Props>()

type Emits = {
    (e: 'refresh'): void
}
const emit = defineEmits<Emits>()

const open = ref(false)
const confirmationOpen = ref(false)
const loading = ref(false)

const { devices } = inject<EventDetailProvider>(EHubProvider.EVENT_DEVICES, {
    devices: ref([])
})

const handleConfirm = () => {
    console.log({ defaultEFiltersDevices })
    loading.value = true
    store
        ?.dispatch(`${EStoreModules.DEVICE}/${EDeviceActions.MASS_UPDATE_DEVICE}`, {
            deviceIds: selectedDevices.value,
            config: defaultEFiltersDevices
        })
        .then(() => {
            emit('refresh')
        })
        .finally(() => (loading.value = false))
}

const selectedDevices = ref<number[]>([])
const handleDeviceSelect = (device: AssociatedDeviceModel | DeviceModel, checked: boolean) =>
    checked
        ? (selectedDevices.value = [...selectedDevices.value, device.device_id ?? device.id!])
        : (selectedDevices.value = selectedDevices.value.filter(item =>
              device.device_id ? item !== device.device_id : item !== device.id
          ))

const handleAllSelect = (checked: boolean) =>
    (selectedDevices.value = checked
        ? ((devices?.value ?? [])?.map(device => device.device_id ?? device.id!) ?? [])
        : [])

const allDevicesSelected = computed(() =>
    devices?.value?.every(item =>
        selectedDevices.value?.find(device =>
            item.device_id ? device === item.device_id : device === item.id
        )
    )
)
</script>

<template>
    <FullScreenLoader :loading="loading" />
    <PrimaryButton
        :label="$t('event.actions.reinitializeFiltersDevicesTitle')"
        @click="open = true" />
    <BaseDialog
        :title="$t('event.actions.reinitializeFiltersDevicesTitle')"
        :open="open"
        @confirm="confirmationOpen = true"
        @close="open = false"
        class="event-checkpoint-component modal-selector-half-width">
        <template #content>
            <StackLayout direction="column" :gap="16">
                <TypographyItem :label="$t('event.actions.reinitializeFiltersDevices')" />
                <StackLayout direction="column" :gap="8">
                    <CheckboxField
                        :modelValue="allDevicesSelected"
                        :indeterminate="!!selectedDevices.length && !allDevicesSelected"
                        label="All"
                        @change="handleAllSelect($event ?? false)" />

                    <StackLayout :gap="4" class="device-checkbox-grid">
                        <CheckboxField
                            v-for="(device, index) in devices"
                            :key="index"
                            :label="device?.name"
                            :modelValue="selectedDevices.includes(device.device_id ?? device.id!)"
                            @change="handleDeviceSelect(device, $event ?? false)"
                            class="device-checkbox" />
                    </StackLayout>
                </StackLayout>
            </StackLayout>
        </template>
    </BaseDialog>
    <BaseDialog
        :title="$t('event.actions.reinitializeFiltersDevicesTitle')"
        :open="confirmationOpen"
        @confirm="handleConfirm()"
        @close="confirmationOpen = false">
        <template #content>
            <StackLayout direction="column" :gap="16">
                <TypographyItem
                    :label="$t('event.actions.reinitializeFiltersDevicesConfirmation')" />
                <ul>
                    <li v-for="(deviceId, index) in selectedDevices" :key="index">
                        <TypographyItem
                            :label="
                                devices?.find(item => deviceId === (item.device_id ?? item.id))
                                    ?.name
                            " />
                    </li>
                </ul>
            </StackLayout>
        </template>
    </BaseDialog>
</template>

<style scoped lang="scss">
.collapse-item {
    & :deep(.collapse-title) {
        width: 100%;
        place-content: space-between;
        box-shadow: 0px 0px 3px 1px $primary;
        padding: 4px;
    }
    & :deep(.collapse-content) {
        padding: 8px;
    }
}

.device-checkbox-grid {
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
}
.device-checkbox {
    white-space: nowrap;
}
</style>
